
import { SQLModule } from '../types';

export const SQL_FUNDAMENTALS_MODULES: SQLModule[] = [
  {
    id: 'sql-basics-1',
    title: 'Introduction to SQL',
    description: 'Learn the fundamentals of SQL and database concepts.',
    content: `# Introduction to SQL

SQL (Structured Query Language) is the standard language for managing relational databases. In this module, you'll learn the core concepts and basic commands.

## What is SQL?

SQL is a programming language designed for managing data held in relational database management systems (RDBMS). It's used to:

- Query data from databases
- Insert, update, and delete records
- Create and modify database structures
- Control access to data

## Key Concepts

### Databases and Tables
- A **database** is a collection of organized data
- A **table** is a collection of related data entries consisting of rows and columns
- Each **row** represents a record
- Each **column** represents a field

### Basic SQL Commands
- **SELECT** - retrieves data from tables
- **INSERT** - adds new records
- **UPDATE** - modifies existing records
- **DELETE** - removes records

## Your First Query

The most basic SQL command is SELECT:

\`\`\`sql
SELECT * FROM users;
\`\`\`

This query retrieves all columns (*) from the users table.`,
    duration: '45 min',
    estimatedTime: 45,
    difficulty: 'beginner',
    order: 1
  },
  {
    id: 'sql-basics-2',
    title: 'Filtering with WHERE',
    description: 'Learn to filter data using WHERE clauses and conditions.',
    content: `# Filtering Data with WHERE

The WHERE clause is used to filter records based on specific conditions. It allows you to retrieve only the data that meets your criteria.

## Basic WHERE Syntax

\`\`\`sql
SELECT column1, column2
FROM table_name
WHERE condition;
\`\`\`

## Common Operators

### Comparison Operators
- **=** Equal to
- **!=** or **<>** Not equal to
- **>** Greater than
- **<** Less than
- **>=** Greater than or equal to
- **<=** Less than or equal to

### Examples

\`\`\`sql
-- Find users older than 25
SELECT * FROM users WHERE age > 25;

-- Find users with specific email
SELECT * FROM users WHERE email = 'john@example.com';

-- Find products under $50
SELECT * FROM products WHERE price < 50;
\`\`\`

## Pattern Matching with LIKE

Use LIKE for pattern matching with wildcards:
- **%** - matches zero or more characters
- **_** - matches exactly one character

\`\`\`sql
-- Find users whose name starts with 'J'
SELECT * FROM users WHERE name LIKE 'J%';

-- Find emails ending with '.com'
SELECT * FROM users WHERE email LIKE '%.com';
\`\`\``,
    duration: '40 min',
    estimatedTime: 40,
    difficulty: 'beginner',
    order: 2
  },
  {
    id: 'sql-basics-3',
    title: 'Sorting with ORDER BY',
    description: 'Learn to sort query results using ORDER BY clause.',
    content: `# Sorting Data with ORDER BY

The ORDER BY clause is used to sort the result set in ascending or descending order.

## Basic Syntax

\`\`\`sql
SELECT column1, column2
FROM table_name
ORDER BY column1 [ASC|DESC];
\`\`\`

## Sorting Options

### Ascending Order (Default)
\`\`\`sql
SELECT * FROM users ORDER BY name;
-- or explicitly
SELECT * FROM users ORDER BY name ASC;
\`\`\`

### Descending Order
\`\`\`sql
SELECT * FROM users ORDER BY age DESC;
\`\`\`

## Multiple Column Sorting

You can sort by multiple columns:

\`\`\`sql
SELECT * FROM users 
ORDER BY age DESC, name ASC;
\`\`\`

This sorts by age (descending) first, then by name (ascending) for users with the same age.

## Practical Examples

\`\`\`sql
-- Get top 5 highest paid employees
SELECT name, salary 
FROM employees 
ORDER BY salary DESC 
LIMIT 5;

-- Sort products by category, then by price
SELECT product_name, category, price 
FROM products 
ORDER BY category, price ASC;
\`\`\``,
    duration: '35 min',
    estimatedTime: 35,
    difficulty: 'beginner',
    order: 3
  }
];
